import {ReactNode} from 'react';

import {
  DatasourceType,
  InclusionDateType,
  Period,
  PeriodType,
  PolicyAmountType,
  PolicyStatusType,
} from 'modules/decisionsNext/api/types';

import {DATE_FORMAT, dayjs, formatDateToString} from 'utils/dateUtils';
import {createEnumMap} from 'utils/createEnumMap';

export const amountTypeLabelMap = createEnumMap(
  {
    [PolicyAmountType.Billed]: 'Billed',
    [PolicyAmountType.Written]: 'ennabl Written',
    [PolicyAmountType.SourceWritten]: 'Written',
    [PolicyAmountType.AnnualizedWritten]: 'Annualized',
  } satisfies Partial<Record<PolicyAmountType, string>>,
  PolicyAmountType,
  ''
);

export const amountTypeOptions = [
  {
    label: amountTypeLabelMap[PolicyAmountType.Billed],
    value: PolicyAmountType.Billed,
    annotation: 'Source system billing transactions in the selected date range.',
  },
  {
    label: amountTypeLabelMap[PolicyAmountType.SourceWritten],
    value: PolicyAmountType.SourceWritten,
    annotation: 'Source system written values of policies incepted in the selected date range.',
  },
  {
    label: amountTypeLabelMap[PolicyAmountType.Written],
    value: PolicyAmountType.Written,
    annotation: 'ennabl written values of policies incepted in the selected date range.',
  },
  {
    label: amountTypeLabelMap[PolicyAmountType.AnnualizedWritten],
    value: PolicyAmountType.AnnualizedWritten,
    annotation: 'Annualized values based on written data in the selected date range',
  },
];

export const baseAmountTypeOptions = amountTypeOptions.filter(
  item => item.value !== PolicyAmountType.AnnualizedWritten
);

export const dataSourceTypeLabelMap = {
  [DatasourceType.AgencyData]: 'Primary Producer',
  [DatasourceType.ProductionCredit]: 'Producer Production Credit',
};
export const productionAllocationOptions = [
  {
    label: dataSourceTypeLabelMap[DatasourceType.ProductionCredit],
    value: DatasourceType.ProductionCredit,
  },
  {
    label: dataSourceTypeLabelMap[DatasourceType.AgencyData],
    value: DatasourceType.AgencyData,
  },
];

export const policyStatusTypeLabelMap = {
  [PolicyStatusType.Ams]: 'AMS Policy Status',
  [PolicyStatusType.Ennabl]: 'ennabl Policy Status',
};
export const policyStatusOptions = [
  {
    label: policyStatusTypeLabelMap[PolicyStatusType.Ams],
    value: PolicyStatusType.Ams,
  },
  {
    label: policyStatusTypeLabelMap[PolicyStatusType.Ennabl],
    value: PolicyStatusType.Ennabl,
  },
];

const amountTypeHelperTexts = createEnumMap(
  {
    [PolicyAmountType.Billed]: 'Source system billing transactions in the selected date range.',
    [PolicyAmountType.SourceWritten]: 'Source system written values of policies incepted in the selected date range.',
    [PolicyAmountType.Written]: 'ennabl written values of policies incepted in the selected date range.',
    [PolicyAmountType.AnnualizedWritten]: 'Annualized values based on written data in the selected date range',
  } satisfies Partial<Record<PolicyAmountType, string>>,
  PolicyAmountType,
  ''
);

export function getAmountTypeHelperText(amountType?: PolicyAmountType | null) {
  if (!amountType) {
    return '';
  }

  return amountTypeHelperTexts[amountType];
}

const datasourceTypeHelperTexts: Record<DatasourceType, string> = {
  [DatasourceType.ProductionCredit]: 'Premium/revenue aggregated by producer production credit.',
  [DatasourceType.AgencyData]: 'Premium/revenue aggregated by primary producer',
};

export function getDatasourceTypeHelperText(datasourceType?: DatasourceType | null) {
  if (!datasourceType) {
    return null;
  }

  return datasourceTypeHelperTexts[datasourceType];
}

export function getPolicyStatusTypeHelperText() {
  return 'Please note that a policy can have more than one status at the same time';
}

export const periodTypeToLabelMap = {
  [PeriodType.Rolling_12Months]: 'Rolling 12',
  [PeriodType.Ytd]: 'YTD',
  [PeriodType.AllTime]: 'All time',
  [PeriodType.FromTo]: 'Custom',
  [PeriodType.InForce]: 'Inforce',
};

const dateRangeOptionMap = {
  [InclusionDateType.Inception]: 'Eff date',
  [InclusionDateType.Expiration]: 'Exp date',
  [InclusionDateType.Transaction]: 'Transaction date',
} satisfies Record<InclusionDateType, string>;

const todayString = formatDateToString();
const startOfTheMonth = dayjs(todayString).date(1);

export const getPeriodTypeSeparatedLabels = (period: Period): ReactNode[] => {
  const inclusionDateType = period.inclusionDateType ? dateRangeOptionMap[period.inclusionDateType] : '';

  switch (period.type) {
    case PeriodType.InForce:
      return !period.timestamp
        ? ['Inforce today']
        : ['Inforce on', dayjs(period.timestamp || todayString).format(DATE_FORMAT)];

    case PeriodType.FromTo:
      return [inclusionDateType, `${dayjs(period.from).format(DATE_FORMAT)} - ${dayjs(period.to).format(DATE_FORMAT)}`];

    case PeriodType.Rolling_12Months:
      return [
        inclusionDateType,
        `${startOfTheMonth.subtract(1, 'y').format(DATE_FORMAT)} - ${startOfTheMonth
          .subtract(1, 'd')
          .format(DATE_FORMAT)}`,
      ];

    default:
      return [inclusionDateType, period.type ? periodTypeToLabelMap[period.type] : ''].filter(Boolean);
  }
};

export const hasInvalidDateRange = (
  from?: string | number | null,
  to?: string | number | null,
  disableFuture: boolean = true
) => {
  if (!from || !to || !dayjs(from).isValid() || !dayjs(to).isValid()) {
    return true;
  }

  const maxDate = disableFuture
    ? formatDateToString(dayjs().add(1, 'day'))
    : formatDateToString(dayjs().add(1, 'day').add(1, 'year'));

  return !dayjs(from).isBefore(to) || !dayjs(from).isBefore(maxDate) || !dayjs(to).isBefore(maxDate);
};
